import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useToasts } from '../../lib/toast';
import type { LoyaltyMemberRow } from '@taj/types';

export function LoyaltyManager({ onBack }: { onBack: () => void }) {
  const push = useToasts((s) => s.push);
  const [enabled, setEnabled] = useState(true);
  const [ppe, setPpe] = useState('1');
  const [discount, setDiscount] = useState('10');
  const [rewards, setRewards] = useState<{ points: string; label: string }[]>([]);
  const [members, setMembers] = useState<LoyaltyMemberRow[]>([]);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);

  const loadMembers = (query: string) => api.loyaltyMembers(query).then(setMembers).catch((e) => push(e.message, 'warn'));

  useEffect(() => {
    api.getLoyaltyConfig().then((c) => {
      setEnabled(c.enabled);
      setPpe(String(c.pointsPerEuro));
      setDiscount(String(c.signupDiscount));
      setRewards(c.rewards.map((r) => ({ points: String(r.points), label: r.label })));
    }).catch((e) => push(e.message, 'warn'));
    loadMembers('');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { const id = setTimeout(() => loadMembers(q), 300); return () => clearTimeout(id); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const c = await api.updateLoyaltyConfig({
        enabled,
        pointsPerEuro: parseInt(ppe, 10) || 0,
        signupDiscount: parseInt(discount, 10) || 0,
        rewards: rewards.map((r) => ({ points: parseInt(r.points, 10) || 0, label: r.label.trim() })).filter((r) => r.points > 0 && r.label),
      });
      setRewards(c.rewards.map((r) => ({ points: String(r.points), label: r.label })));
      push('✅ Fidélité enregistrée');
    } catch (e: any) { push(e.message, 'warn'); }
    finally { setSaving(false); }
  };

  const toggleEnabled = async () => {
    const v = !enabled;
    setEnabled(v);
    try { await api.updateLoyaltyConfig({ enabled: v }); push(v ? '🎁 Fidélité activée' : '⛔ Fidélité désactivée'); }
    catch (e: any) { push(e.message, 'warn'); setEnabled(!v); }
  };

  const label = (t: string) => <div style={{ fontSize: '.72rem', fontWeight: 800, color: 'var(--ink-dim)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 5 }}>{t}</div>;

  return (
    <div className="staff-scroll">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 14px' }}>
        <button className="km-back" onClick={onBack}>←</button>
        <div className="staff-title" style={{ fontSize: '1.4rem', flex: 1 }}>🎁 Fidélité</div>
        <div className="al-toggle" style={{ padding: '6px 12px', margin: 0, gap: 8 }}>
          <span className="an" style={{ fontSize: '.82rem', fontWeight: 800, color: enabled ? 'var(--green)' : 'var(--ink-faint)' }}>{enabled ? 'Activée' : 'Désactivée'}</span>
          <div className={`al-sw${enabled ? ' on' : ''}`} onClick={toggleEnabled} />
        </div>
      </div>
      {!enabled && <div className="cc" style={{ marginBottom: 14, background: '#fdecec', border: '1px solid #f2b8b8', color: '#c33', fontWeight: 700, fontSize: '.85rem' }}>⛔ Programme fidélité désactivé : aucune remise ni point n'est appliqué, et la carte fidélité est masquée sur la borne et la caisse.</div>}

      {/* Réglages */}
      <div className="cc" style={{ marginBottom: 16, maxWidth: 620 }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div>{label('Points gagnés par euro dépensé')}
            <input className="inp" inputMode="numeric" style={{ width: 120 }} value={ppe} onChange={(e) => setPpe(e.target.value)} />
            <div style={{ fontSize: '.72rem', color: 'var(--ink-faint)', marginTop: 3 }}>Ex. 1 → 1 point par € (20 € = 20 pts)</div>
          </div>
          <div>{label('Remise à l’inscription (%)')}
            <input className="inp" inputMode="numeric" style={{ width: 120 }} value={discount} onChange={(e) => setDiscount(e.target.value)} />
            <div style={{ fontSize: '.72rem', color: 'var(--ink-faint)', marginTop: 3 }}>Remise pour un membre sur sa commande</div>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          {label('🎁 Récompenses (paliers de points)')}
          <div style={{ fontSize: '.78rem', color: 'var(--ink-faint)', marginBottom: 8 }}>Définis ce que le client obtient à partir d’un certain nombre de points.</div>
          {rewards.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <input className="inp" inputMode="numeric" style={{ width: 90 }} placeholder="Points" value={r.points}
                onChange={(e) => setRewards(rewards.map((x, j) => (j === i ? { ...x, points: e.target.value } : x)))} />
              <span style={{ color: 'var(--ink-dim)', fontWeight: 700 }}>pts →</span>
              <input className="inp" style={{ flex: '1 1 200px', minWidth: 160 }} placeholder="Récompense (ex. Boisson offerte)" value={r.label}
                onChange={(e) => setRewards(rewards.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              <button type="button" title="Supprimer" onClick={() => setRewards(rewards.filter((_, j) => j !== i))}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '1.05rem' }}>🗑️</button>
            </div>
          ))}
          <button type="button" onClick={() => setRewards([...rewards, { points: '', label: '' }])}
            style={{ marginTop: 2, padding: '8px 12px', borderRadius: 10, border: '1px dashed var(--gold-line)', background: 'var(--gold-soft)', color: 'var(--gold)', fontWeight: 800, fontSize: '.82rem', cursor: 'pointer' }}>
            ➕ Ajouter une récompense
          </button>
        </div>

        <button className="btn-gold" style={{ marginTop: 14 }} disabled={saving} onClick={save}>💾 ENREGISTRER</button>
      </div>

      {/* Membres */}
      <div className="cc" style={{ maxWidth: 620 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <div className="staff-title" style={{ fontSize: '1.1rem', flex: 1 }}>👥 Membres ({members.length})</div>
          <input className="inp" style={{ flex: '1 1 200px' }} placeholder="🔎 Nom, téléphone ou code…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {members.length === 0 && <div style={{ fontSize: '.85rem', color: 'var(--ink-faint)' }}>Aucun membre pour l’instant. Les clients s’inscrivent depuis la borne, la caisse ou le site (bouton « Rejoindre »).</div>}
        {members.map((m) => (
          <div key={m.loyaltyCode ?? m.phone} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1rem', color: 'var(--gold)', minWidth: 76 }}>{m.loyaltyCode}</span>
            <div style={{ flex: '1 1 140px', minWidth: 120 }}>
              <div style={{ fontWeight: 800 }}>{m.name || '—'}</div>
              <div style={{ fontSize: '.78rem', color: 'var(--ink-faint)' }}>📞 {m.phone || '—'}</div>
            </div>
            <span style={{ fontWeight: 900, color: 'var(--green)', fontSize: '1.05rem' }}>{m.points} pts</span>
          </div>
        ))}
      </div>
    </div>
  );
}
